import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';

interface Props {
    open: boolean;
    onClose: () => void;
    paymentId: string;
    onSuccess: () => void;
}

const MAX_REASON_LENGTH = 500;

const SkipJournalDialog: React.FC<Props> = ({ open, onClose, paymentId, onSuccess }) => {
    const { showSuccess, showApiError } = useToast();
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    const handleSubmit = async () => {
        if (!reason.trim()) {
            showApiError(new Error('Reason required'), 'Please provide a reason for skipping');
            return;
        }
        setSubmitting(true);
        try {
            await PayrollService.skipJournalPost(paymentId, reason.trim());
            showSuccess('Journal post marked as skipped');
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, 'Failed to skip journal post');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.skipForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Skip Journal Post</DialogTitle>
            <DialogContent dividers data-help-section="payroll.skipForm.section.details">
                <Box sx={{ pt: 1 }}>
                    <TextField
                        data-help="payroll.skipForm.field.reason" label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        autoFocus
                        inputProps={{ maxLength: MAX_REASON_LENGTH }}
                        helperText={`Admin override: this skips journal posting for this payment and cannot be undone. ${reason.length}/${MAX_REASON_LENGTH}`}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.skipForm.cancel" onClick={onClose} disabled={submitting}>Cancel</Button>
                <Button
                    data-help="payroll.skipForm.save"
                    {...helpWhy(submitting ? { kind: 'status', message: 'Saving is in progress.' } : !reason.trim() ? { kind: 'status', message: 'Enter a reason first.' } : null)}
                    onClick={handleSubmit}
                    variant="contained"
                    color="warning"
                    disabled={submitting || !reason.trim()}
                >
                    {submitting ? 'Skipping...' : 'Skip'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SkipJournalDialog;
